import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import st from "@/styles/Ui.module.scss";
import clsx from "clsx";
import DashboardClass from "@/utils/dashboard_class.ts";
import ChevroneIcon from "@/svg/ChevroneIcon.tsx";
import StandartSelector from "@/ui/StandartSelector.tsx";
import {Option} from "@mui/joy";
import CommonClass from "@/utils/common.ts";
import {CircularProgress, TextField} from "@mui/material";
import {textfield_sx} from "@/utils/mui_sx.ts";
import useLoans from "@/hooks/loans.ts";
import PaymentMethodsObjInterface from "@/types_and_interfaces/payment_methods_obj_interface.ts";
import PaymentMethodInterface from "@/types_and_interfaces/payment_method_interface.ts";
import {useLocation} from "react-router-dom";
import PhoneNumberInput from "@/ui/PhoneNumberInput.tsx";
import auth from "@/hooks/auth.ts";
import {country_code} from "@/utils/constants.ts";


interface MethodProps {
    paymentMethod: keyof PaymentMethodsObjInterface,
    paymentMethodHandler(x: string): void
    all_methods:PaymentMethodsObjInterface | undefined | {}
}


interface Props {
    paymentMethod:keyof PaymentMethodsObjInterface
    paymentMethodHandler(x:keyof PaymentMethodsObjInterface):void
    formErrors:any,
    initBody?: any
}

const PaymentMethodSwitcherMain:React.FC<Props> = (props) => {
    const {
        paymentMethod,
        paymentMethodHandler,
        formErrors,
        initBody
    } = props
    
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsObjInterface | undefined>(undefined);
    const [recaptchaReady, setRecaptchaReady] = useState<boolean>(false);
    const loans = useLoans()
    
    const {t} = useTranslation();
    const isSunday = new Date().getDay() === 0
    const needVerifyDay = CommonClass.general_settings?.extra_functionality?.M2026_05_07_need_to_verify_payment_day
    const hasMia = !!paymentMethods && "mia" in (paymentMethods as object)

    const showMia = (hasMia && needVerifyDay && isSunday) || !needVerifyDay
    const showCash = (needVerifyDay && !isSunday) || !needVerifyDay
    const showOtherKeys = (needVerifyDay && !isSunday) || !needVerifyDay

    useEffect(() => {
        const loadRecaptcha = async () => {
            if(typeof CommonClass.payment_methods_obj === "undefined") {
                let count = 0
                let interval = setInterval(() => {
                    count += 1
                    
                    if(count > 20){
                        clearInterval(interval)
                    }
                    
                    if(typeof window.grecaptcha !== undefined) {
                        
                        window.grecaptcha?.ready(() => {
                            setRecaptchaReady(true);
                        });
                        
                        clearInterval(interval)
                    }
                    
                }, 200)
                

            } else setRecaptchaReady(true);
        };

        if(CommonClass.general_settings.recaptcha.is_enabled) {
            loadRecaptcha();
        } else setRecaptchaReady(true)

    }, []);
    
    useEffect(() => {
        const set_methods = async  () => {
            const methods = await loans.getMethods()
            
            if(typeof methods !== undefined && Object.keys(methods).length) {

                paymentMethodHandler((!needVerifyDay || (needVerifyDay && isSunday)) ? "mia" : "cash")

            }
            
            setPaymentMethods(methods);
        }
        
        if(recaptchaReady) {
            set_methods()
        }
        
    }, [recaptchaReady]);


    return (
        <div className={st.method_container}>
            
            <span className={st.label}>{t("money_recieve_method")}</span>
            
            {(typeof paymentMethods === "object" && Object.keys(paymentMethods).length) ? (
                <>
                    <PaymentMethodSwitcher
                        all_methods={paymentMethods}
                        paymentMethod={paymentMethod as keyof PaymentMethodsObjInterface}
                        paymentMethodHandler={paymentMethodHandler}
                    />

                    {!!paymentMethod && (
                        <>
                            {paymentMethod === 'mia' && showMia && (
                                <MiaContainer initBody={initBody}/>
                            )}

                            {paymentMethod === 'cash' && showCash && (
                                <CashContainer/>
                            )}

                            {paymentMethod === "online_wallet" && showOtherKeys && (
                                <ChooseWallet
                                    local_method={paymentMethods[paymentMethod] as PaymentMethodInterface[]}
                                    method_key={paymentMethod}
                                    formErrors={formErrors}
                                />
                            )}

                            {paymentMethod === "bank" && showOtherKeys && (
                                <ChooseWallet
                                    local_method={paymentMethods[paymentMethod] as PaymentMethodInterface[]}
                                    method_key={paymentMethod}
                                    formErrors={formErrors}
                                />
                            )}
                        </>
                    )}
                </>
            ) : null}
            
            {((typeof paymentMethods === "object" && !Object.keys(paymentMethods).length)) ? (
                <strong className={st.error}>
                    {t("error_message")}
                </strong>
            ) : null}
            
            {typeof paymentMethods === "undefined" && (
                <div className={st.loading_container}>
                    <CircularProgress color={"success"} size={24} />
                </div>
            )}
        </div>
    )
}



const PaymentMethodSwitcher: React.FC<MethodProps> = ({ paymentMethod, paymentMethodHandler, all_methods }) => {
    const location = useLocation()
    const { t } = useTranslation()
    const isSunday = new Date().getDay() === 0
    const needVerifyDay = CommonClass.general_settings?.extra_functionality?.M2026_05_07_need_to_verify_payment_day

    const is_apply_credit = location.pathname.includes(DashboardClass.dashboard_routes.ApplyCredit)
    const other_keys = (typeof all_methods === "object" ? Object.keys(all_methods) : []).filter(s => s !== "mia")
    const hasMia = !!all_methods && "mia" in (all_methods as object)

    const showMia = (hasMia && needVerifyDay && isSunday) || !needVerifyDay
    const showCash = (needVerifyDay && !isSunday) || !needVerifyDay
    const showOtherKeys = (needVerifyDay && !isSunday) || !needVerifyDay

    return (
        <div className={st.methods_switcher}>
            {showMia && (
                <button
                    onClick={() => paymentMethodHandler("mia")}
                    type="button"
                    className={clsx(st.methods_switcher_item, "mia" === paymentMethod && st.selected)}
                >
                    {t("mia")}
                </button>
            )}
            {showCash && (
                <button
                    onClick={() => paymentMethodHandler("cash")}
                    type="button"
                    className={clsx(st.methods_switcher_item, "cash" === paymentMethod && st.selected)}
                >
                    {t("cash")}
                </button>
            )}
            {showOtherKeys && other_keys.map((s: string, i: number) => (
                <button
                    onClick={() => paymentMethodHandler(s)}
                    style={is_apply_credit && s === "refinancing" ? {display: "none"} : {}}
                    type="button"
                    key={`methods_switcher_item_${i}`}
                    className={clsx(st.methods_switcher_item, s === paymentMethod && st.selected)}
                >
                    {t(s)}
                </button>
            ))}
        </div>
    )
}

const CashContainer = () => {
    
    let {t} = useTranslation()
    // const navigate = useNavigate()
    
    return (
        <div className={st.cash_container}>
            <span>
                {t("rise_at_office")}
            </span>
            
            <button
                onClick={() => {
                    const apply_data = sessionStorage.getItem(CommonClass.local_storage_fields.apply_credit_form_body)
                    
                    if(apply_data) {
                        localStorage.setItem(CommonClass.local_storage_fields.reserve_data, apply_data)
                    }
                    
                    let timeout = setTimeout(() => {
                        window.open(DashboardClass.dashboard_routes.Locations, '_blank');
                        clearTimeout(timeout)
                    }, 200)
                    
                }}
                type={"button"}
            >
                {t("view_offices_list")} <ChevroneIcon/>
            </button>
        </div>
    )
}

interface ChooseWalletProps {
    local_method: Array<any>
    method_key:keyof PaymentMethodsObjInterface
    formErrors: any
}

const ChooseWallet:React.FC<ChooseWalletProps> = (props:any) => {
    
    const {
        local_method,
        formErrors
    } = props
    
    const {t} = useTranslation()
    const [selected, setSelected] = useState("");
    const [characters, set_characters] = useState<number>(0)
    const max_characters = 24
    
    const get_code_or_name = () => {
        
        if(selected.length) {
            
            const m = local_method.find((x:any) => x.name === selected)
            
            return m
            
        } else return undefined
    }
    
    const method = get_code_or_name()
    const label_generation = () => {
        
        if(!method) return ""
        
        if(method.iban_disb_shop.length && method.phone_disb_shop.length) {
            return `${t("phone")}/${t("iban")}`
            
        } else if(method.iban_disb_shop.length && !method.phone_disb_shop.length){
            return `${t("iban")} ${characters ?`${characters}/${max_characters} ${t("characters")}` : ""}`
            
        } else if(!method.iban_disb_shop.length && method.phone_disb_shop.length){
            return `${t("phone")}`
            
        }  else if(!method.iban_disb_shop.length && !method.phone_disb_shop.length){
            return `${t("phone")}`
            
        } else return  ""
        
    }
    
    const handleChange = (e: any) => {
        if (e === null) return;

        let target = e.target.textContent
        setSelected(target)
    };
    
    useEffect(() => {
        setSelected(local_method[0].name)
    }, []);
    
    return (
        <div className={st.inputs}>

            {(Array.isArray(local_method) && local_method) ? (
                <>
                
                    <StandartSelector
                        handleChange={handleChange}
                        selected={selected}
                        name={"bank"}
                        // error={(Boolean(formErrors.hasOwnProperty("refund_bank"))).toString()}
                    >
                        
                        {local_method.map((x) => {
                            
                            let obj = x as PaymentMethodInterface
                            
                            return (
                                <Option
                                    key={`${obj.name} ${obj.id}`}
                                    value={`${obj.name}`}
                                >
                                    {obj.name}
                                </Option>
                            )
                        })}
    
                    </StandartSelector>
                    
                    {(label_generation() === t("phone")) ? (
                        <PhoneNumberInput
                            error={Boolean(formErrors.hasOwnProperty("refund_account"))}
                            name={"refund_account"}
                        
                        />
                    ) : (
                        <TextField
                            name={"refund_account"}
                            label={label_generation()}
                            color={"success"}
                            sx={textfield_sx}
                            error={Boolean(formErrors.hasOwnProperty("refund_account"))}
                            onChange={(e) => {
                                let newValue = e.target.value.toUpperCase();
                                if (newValue.length > max_characters) {
                                    newValue = newValue.slice(0, max_characters);
                                }
                                e.target.value = newValue;
                                set_characters(e.target.value.length)
                            }}
                            
                        />
                    )}
                    
                    <input defaultValue={method?.code.length ? method?.code : method?.name} type="hidden" name={"refund_bank"} />
                    <input defaultValue={method?.iban_disb_shop} type="hidden" name={"iban_disb_shop"} />
                    <input defaultValue={method?.identifier} type="hidden" name={"identifier"} />
                    <input defaultValue={method?.phone_disb_shop} type="hidden" name={"phone_disb_shop"} />
                </>
            ) : null}

        </div>
    )
}

interface MiaProps {
    initBody: any
}

const MiaContainer: React.FC<MiaProps> = ({initBody}) => {
    const {t} = useTranslation()
    const isSunday = new Date().getDay() === 0

    const user_phone = auth().current_user()?.phone
    const formattedPhone = (typeof user_phone === "string" && user_phone.length) ?
        CommonClass.formatNumber(user_phone.replace(country_code, "")) :
        initBody.phone || ""
    const phone = `${country_code} ${formattedPhone}`

    return (
        <div className={clsx(st.cash_container, st.mia)}>
            <div className={clsx(st.mia_message, {[st.margin]: !isSunday})}>
                {isSunday && <p>{t("mia_transfer_sunday_message")}</p>}
                <p>{t("mia_transfer_payment", {phone})}</p>
                <p>{t("mia_transfer_active")}</p>
            </div>
        </div>
    )
}

export default PaymentMethodSwitcherMain